// Timezone mapping for US regions
export const US_TIMEZONES = {
  'ET': 'America/New_York',
  'CT': 'America/Chicago',
  'MT': 'America/Denver',
  'PT': 'America/Los_Angeles',
  'AKT': 'America/Anchorage',
  'HT': 'Pacific/Honolulu',
  'AZ': 'America/Phoenix'
};

/**
 * Gets the provider's timezone based on stored preference or browser detection
 * @returns {string} IANA timezone name (e.g., 'America/New_York')
 */
export const getProviderTimezone = () => {
  // Get timezone from session storage
  const storedTimezone = sessionStorage.getItem('providerTimezone');
  if (storedTimezone && US_TIMEZONES[storedTimezone]) {
    return US_TIMEZONES[storedTimezone];
  }
  
  // If no timezone is stored, use browser's timezone
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Check if browser timezone matches any of our US timezones
  for (const [key, value] of Object.entries(US_TIMEZONES)) {
    if (browserTimezone === value) {
      // Store the detected timezone for future use
      sessionStorage.setItem('providerTimezone', key);
      return value;
    }
  }
  
  // If no match found in US timezones, use the browser's timezone directly
  return browserTimezone;
};

/**
 * Sets the provider's timezone preference
 * @param {string} timezoneKey - The timezone key (e.g., 'ET', 'CT', etc.)
 * @returns {boolean} Whether the timezone was successfully set
 */
export const setProviderTimezone = (timezoneKey) => {
  if (US_TIMEZONES[timezoneKey]) {
    sessionStorage.setItem('providerTimezone', timezoneKey);
    return true;
  }
  return false;
};

/**
 * Gets a list of available US timezones with their display names
 * @returns {Array<{key: string, value: string, label: string}>} Array of timezone objects
 */
export const getAvailableTimezones = () => {
  return Object.entries(US_TIMEZONES).map(([key, value]) => ({
    key,
    value,
    label: getTimezoneDisplayName(key)
  }));
};

/**
 * Gets a human-readable display name for a timezone
 * @param {string} timezoneKey - The timezone key (e.g., 'ET', 'CT', etc.)
 * @returns {string} Human-readable timezone name
 */
export const getTimezoneDisplayName = (timezoneKey) => {
  const displayNames = {
    'ET': 'Eastern Time (ET)',
    'CT': 'Central Time (CT)',
    'MT': 'Mountain Time (MT)',
    'PT': 'Pacific Time (PT)',
    'AKT': 'Alaska Time (AKT)',
    'HT': 'Hawaii Time (HT)',
    'AZ': 'Arizona Time (AZ)'
  };
  return displayNames[timezoneKey] || timezoneKey;
}; 